/**
 * DriveBranchRestructurePanel - Iter 113.
 *
 * Restructure la racine Google Drive vers une hierarchie par BRANCHE :
 *   - "Bretagne - Projets/"           (projets actifs Bretagne)
 *   - "Bretagne - Dossiers clients/"  (leads Bretagne sans projet, dossier leger)
 *   - "Hauts-de-France - Projets/"
 *   - "Hauts-de-France - Dossiers clients/"
 *
 * Etapes : Preview (dry-run) -> Selection -> Execute. Reserve aux CEO / Admin.
 */
import React, { useState, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Badge = ({ children, color = 'gray' }) => (
  <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded bg-${color}-100 text-${color}-700 font-semibold`}>
    {children}
  </span>
);

const DriveBranchRestructurePanel = () => {
  const [plan, setPlan] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [result, setResult] = useState(null);
  const [selectedProjects, setSelectedProjects] = useState({});
  const [selectedLeads, setSelectedLeads] = useState({});
  const [error, setError] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    setError(null);
    setResult(null);
    try {
      const r = await axios.get(`${API}/admin/drive/branch-restructure/preview`);
      setPlan(r.data);
      const ip = {};
      (r.data.projects || []).forEach((it) => { ip[it.project_id] = true; });
      setSelectedProjects(ip);
      const il = {};
      (r.data.leads || []).forEach((it) => { il[it.lead_id] = !it.already_exists; });
      setSelectedLeads(il);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setLoadingPreview(false); }
  }, []);

  const execute = async () => {
    const pIds = Object.keys(selectedProjects).filter((k) => selectedProjects[k]);
    const lIds = Object.keys(selectedLeads).filter((k) => selectedLeads[k]);
    if (pIds.length === 0 && lIds.length === 0) {
      alert('Selectionnez au moins un projet ou un lead.');
      return;
    }
    if (!window.confirm(
      `Vous allez REORGANISER ${pIds.length} projet(s) et CREER ${lIds.length} dossier(s) client leger(s) sur Google Drive.\n\nLes dossiers seront DEPLACES sous "[Branche] - Projets/" ou "[Branche] - Dossiers clients/".\n\nConfirmer ?`
    )) return;

    setExecuting(true);
    setError(null);
    try {
      const r = await axios.post(`${API}/admin/drive/branch-restructure/execute`, {
        apply_projects: pIds.length > 0,
        apply_leads: lIds.length > 0,
        project_ids: pIds,
        lead_ids: lIds,
      });
      setResult(r.data);
      setTimeout(() => loadPreview(), 800);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally { setExecuting(false); }
  };

  const toggleAllProjects = (v) => {
    const n = {};
    (plan?.projects || []).forEach((it) => { n[it.project_id] = v; });
    setSelectedProjects(n);
  };
  const toggleAllLeads = (v) => {
    const n = {};
    (plan?.leads || []).forEach((it) => { n[it.lead_id] = v; });
    setSelectedLeads(n);
  };

  const totalSelected =
    Object.values(selectedProjects).filter(Boolean).length +
    Object.values(selectedLeads).filter(Boolean).length;

  return (
    <div className="realease-card p-6" data-testid="drive-branch-restructure-panel">
      <div className="mb-4">
        <h3 className="font-semibold text-gray-900 text-lg flex items-center gap-2">
          <svg className="w-5 h-5 text-[#13736D]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          Restructuration Drive par branche
          <Badge color="amber">ITER 113</Badge>
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Reorganise la racine Drive en 4 sections : <code className="bg-gray-100 px-1 rounded">Bretagne - Projets</code>,{' '}
          <code className="bg-gray-100 px-1 rounded">Bretagne - Dossiers clients</code>,{' '}
          <code className="bg-gray-100 px-1 rounded">Hauts-de-France - Projets</code>,{' '}
          <code className="bg-gray-100 px-1 rounded">Hauts-de-France - Dossiers clients</code>.
          Les dossiers clients (leads) recoivent une structure legere (Pieces fournies + Qualification).
          Lors de la promotion en projet, le dossier sera DEPLACE et complete des sous-dossiers projet.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <button
          type="button"
          onClick={loadPreview}
          disabled={loadingPreview}
          className="px-4 py-2 rounded-md text-sm font-semibold bg-[#13736D] text-white hover:bg-[#0f5e58] disabled:opacity-50 transition"
          data-testid="drive-branch-preview-btn"
        >
          {loadingPreview ? 'Analyse...' : 'Analyser la migration par branche'}
        </button>
        {plan && (
          <button
            type="button"
            onClick={execute}
            disabled={executing || totalSelected === 0}
            className="px-4 py-2 rounded-md text-sm font-semibold bg-[#A57F4F] text-white hover:bg-[#8d6a40] disabled:opacity-50 transition"
            data-testid="drive-branch-execute-btn"
          >
            {executing ? 'Migration en cours...' : `Migrer ${totalSelected} element(s)`}
          </button>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-sm text-red-700" data-testid="drive-branch-error">
          Erreur : {error}
        </div>
      )}

      {plan && (
        <div className="space-y-6">
          {/* PROJETS */}
          <div data-testid="drive-branch-projects-section">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-800 text-sm">
                Projets ({(plan.projects || []).length})
              </h4>
              {(plan.projects || []).length > 0 && (
                <div className="flex gap-2 text-xs">
                  <button type="button" onClick={() => toggleAllProjects(true)} className="text-[#13736D] hover:underline">Tout</button>
                  <span className="text-gray-300">·</span>
                  <button type="button" onClick={() => toggleAllProjects(false)} className="text-gray-500 hover:underline">Rien</button>
                </div>
              )}
            </div>
            {(plan.projects || []).length === 0 ? (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                Aucun projet a reorganiser.
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 w-6"></th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Projet</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Branche</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Section cible</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Nom cible</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(plan.projects || []).map((it) => (
                      <tr key={it.project_id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={!!selectedProjects[it.project_id]}
                            onChange={(e) => setSelectedProjects({ ...selectedProjects, [it.project_id]: e.target.checked })}
                            data-testid={`drive-branch-project-${it.project_id}`}
                          />
                        </td>
                        <td className="px-2 py-2 font-medium text-gray-900">{it.project_name || it.project_id}</td>
                        <td className="px-2 py-2">
                          <Badge color={it.branch === 'nord' ? 'blue' : 'emerald'}>{it.branch}</Badge>
                        </td>
                        <td className="px-2 py-2 text-gray-600">{it.target_section}</td>
                        <td className="px-2 py-2 font-mono text-[#13736D] break-all">{it.target_name}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* LEADS */}
          <div data-testid="drive-branch-leads-section">
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-semibold text-gray-800 text-sm">
                Leads en attente ({(plan.leads || []).length})
              </h4>
              {(plan.leads || []).length > 0 && (
                <div className="flex gap-2 text-xs">
                  <button type="button" onClick={() => toggleAllLeads(true)} className="text-[#13736D] hover:underline">Tout</button>
                  <span className="text-gray-300">·</span>
                  <button type="button" onClick={() => toggleAllLeads(false)} className="text-gray-500 hover:underline">Rien</button>
                </div>
              )}
            </div>
            {(plan.leads || []).length === 0 ? (
              <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-700">
                Aucun lead a creer.
              </div>
            ) : (
              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-2 py-2 w-6"></th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Lead</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Branche</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Section</th>
                      <th className="px-2 py-2 text-left font-semibold text-gray-700">Nom dossier</th>
                      <th className="px-2 py-2 text-center font-semibold text-gray-700">Etat</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(plan.leads || []).map((it) => (
                      <tr key={it.lead_id} className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50">
                        <td className="px-2 py-2">
                          <input
                            type="checkbox"
                            checked={!!selectedLeads[it.lead_id]}
                            onChange={(e) => setSelectedLeads({ ...selectedLeads, [it.lead_id]: e.target.checked })}
                            disabled={it.already_exists}
                            data-testid={`drive-branch-lead-${it.lead_id}`}
                          />
                        </td>
                        <td className="px-2 py-2 font-medium text-gray-900">
                          {it.name}
                          <div className="text-[10px] text-gray-400">{it.email}</div>
                        </td>
                        <td className="px-2 py-2">
                          <Badge color={it.branch === 'nord' ? 'blue' : 'emerald'}>{it.branch}</Badge>
                        </td>
                        <td className="px-2 py-2 text-gray-600">{it.target_section}</td>
                        <td className="px-2 py-2 font-mono text-[#13736D] break-all">{it.target_name}</td>
                        <td className="px-2 py-2 text-center">
                          {it.already_exists ? <Badge color="emerald">CREE</Badge> : <Badge color="amber">A CREER</Badge>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* LEADS IGNORES */}
          {(plan.skipped_unassigned_leads || []).length > 0 && (
            <div data-testid="drive-branch-skipped-section">
              <h4 className="font-semibold text-gray-800 text-sm mb-2">
                Leads ignores - branche non assignee ({plan.skipped_unassigned_leads.length})
              </h4>
              <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-800">
                Ces leads n&apos;ont pas de branche assignee. Assignez-les manuellement avant migration.
                <ul className="mt-1 list-disc list-inside">
                  {plan.skipped_unassigned_leads.slice(0, 10).map((l) => (
                    <li key={l.lead_id}>{l.name || l.lead_id} <span className="text-gray-500">({l.email})</span></li>
                  ))}
                  {plan.skipped_unassigned_leads.length > 10 && (
                    <li className="text-gray-500">+{plan.skipped_unassigned_leads.length - 10} autres...</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
      )}

      {result && (
        <div className="mt-4 p-4 rounded-md bg-emerald-50 border border-emerald-200" data-testid="drive-branch-result">
          <p className="font-semibold text-emerald-800 mb-2">
            Migration terminee - {(result.applied?.projects || []).length} projet(s), {(result.applied?.leads || []).length} lead(s).
          </p>
          {(result.applied?.errors || []).length > 0 && (
            <div className="text-xs text-red-700">
              <p className="font-semibold">Erreurs :</p>
              <ul className="list-disc list-inside">
                {result.applied.errors.map((er, i) => (
                  <li key={i}>{er.type} {er.id} : {er.reason || `rename=${er.rename_ok} move=${er.move_ok}`}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <AdoptFolderForm />
    </div>
  );
};

/**
 * Sous-formulaire : adopter un dossier Drive existant et le lier a un projet.
 * Le dossier source doit etre dans le MEME Drive RealEase (option 1a CEO).
 * Si le projet a deja un dossier mappe, l'ancien est archive (option 2b).
 * Le dossier adopte est deplace + renomme + sous-dossiers projet completes (option 3a).
 */
const AdoptFolderForm = () => {
  const [projects, setProjects] = React.useState([]);
  const [projectId, setProjectId] = React.useState('');
  const [folderInput, setFolderInput] = React.useState('');
  const [targetNameOverride, setTargetNameOverride] = React.useState('');
  const [preview, setPreview] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  const [res, setRes] = React.useState(null);

  React.useEffect(() => {
    axios.get(`${API}/projects`).then((r) => {
      const list = (r.data || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setProjects(list);
    }).catch(() => {});
  }, []);

  const runDryRun = async () => {
    setErr(null); setRes(null); setPreview(null);
    if (!projectId || !folderInput) { setErr('Selectionnez un projet et collez l\u2019ID/URL du dossier.'); return; }
    setBusy(true);
    try {
      const body = {
        project_id: projectId,
        folder_id_or_url: folderInput.trim(),
        dry_run: true,
      };
      if (targetNameOverride.trim()) body.target_name_override = targetNameOverride.trim();
      const r = await axios.post(`${API}/admin/drive/branch-restructure/adopt-folder`, body);
      setPreview(r.data);
      // Pre-remplit le champ nom cible avec la valeur auto pour edition facile
      if (!targetNameOverride && r.data?.would_adopt?.auto_built_name) {
        setTargetNameOverride(r.data.would_adopt.auto_built_name);
      }
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const runAdopt = async () => {
    if (!preview) { runDryRun(); return; }
    const wa = preview.would_adopt || {};
    const prev = preview.previous_mapping;
    const finalName = targetNameOverride.trim() || wa.target_name;
    const msg =
      `Confirmer adoption :\n\n` +
      `Dossier source : "${wa.current_name}" (${wa.folder_id})\n` +
      `   -> sera deplace dans "${wa.target_section}"\n` +
      `   -> sera renomme "${finalName}"\n` +
      (prev?.root_folder_id
        ? `\nL'ancien dossier de ce projet ("${prev.project_name || prev.root_folder_id}") sera ARCHIVE dans "_Archives - Anciens dossiers/".\n`
        : '') +
      `\nContinuer ?`;
    if (!window.confirm(msg)) return;
    setErr(null); setRes(null); setBusy(true);
    try {
      const body = {
        project_id: projectId,
        folder_id_or_url: folderInput.trim(),
        dry_run: false,
      };
      if (targetNameOverride.trim() && targetNameOverride.trim() !== wa.auto_built_name) {
        body.target_name_override = targetNameOverride.trim();
      }
      const r = await axios.post(`${API}/admin/drive/branch-restructure/adopt-folder`, body);
      setRes(r.data);
      setPreview(null);
      setFolderInput('');
      setTargetNameOverride('');
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 pt-6 border-t border-gray-200" data-testid="drive-adopt-form">
      <h4 className="font-semibold text-gray-800 text-sm mb-1">
        Adopter un dossier Drive existant
        <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-semibold">ITER 113D</span>
      </h4>
      <p className="text-xs text-gray-500 mb-3">
        Colle l&apos;URL ou l&apos;ID d&apos;un dossier deja present dans le Drive RealEase, choisis un projet :
        il sera <b>deplace</b> dans la section &laquo;&nbsp;[Branche] - Projets&nbsp;&raquo;, <b>renomme</b> au format
        &laquo;&nbsp;VILLE - NOM Prenom&nbsp;&raquo; et complete des sous-dossiers manquants. Si le projet
        avait deja un dossier lie, l&apos;ancien sera archive.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 mb-1">Projet RealEase</label>
          <select
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setPreview(null); setRes(null); setTargetNameOverride(''); }}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
            data-testid="adopt-project-select"
          >
            <option value="">-- Choisir un projet --</option>
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.name || p.project_id} {p.branch ? `(${p.branch})` : ''}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-semibold text-gray-600 mb-1">URL ou ID du dossier Drive</label>
          <input
            type="text"
            value={folderInput}
            onChange={(e) => { setFolderInput(e.target.value); setPreview(null); setRes(null); }}
            placeholder="https://drive.google.com/drive/folders/1AbC..."
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 font-mono"
            data-testid="adopt-folder-input"
          />
        </div>
      </div>

      <div className="mt-3">
        <label className="block text-[11px] font-semibold text-gray-600 mb-1">
          Nom cible du dossier
          <span className="ml-1 font-normal text-gray-400">
            (format &laquo;&nbsp;VILLE - NOM Prenom&nbsp;&raquo;. Modifiable si la ville du projet est manquante ou incorrecte.)
          </span>
        </label>
        <input
          type="text"
          value={targetNameOverride}
          onChange={(e) => setTargetNameOverride(e.target.value)}
          placeholder="Ex: RENNES - DUPONT Jean (rempli automatiquement apres 'Previsualiser')"
          className="w-full text-sm border border-gray-300 rounded px-2 py-1.5"
          data-testid="adopt-target-name"
        />
      </div>

      <div className="flex gap-2 mt-3">
        <button
          type="button"
          onClick={runDryRun}
          disabled={busy || !projectId || !folderInput}
          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50 transition"
          data-testid="adopt-preview-btn"
        >
          {busy && !preview ? '...' : 'Previsualiser'}
        </button>
        <button
          type="button"
          onClick={runAdopt}
          disabled={busy || !preview}
          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-[#A57F4F] text-white hover:bg-[#8d6a40] disabled:opacity-50 transition"
          data-testid="adopt-execute-btn"
        >
          {busy && preview ? 'Adoption en cours...' : 'Confirmer adoption'}
        </button>
      </div>

      {err && (
        <div className="mt-3 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700" data-testid="adopt-error">
          {err}
        </div>
      )}

      {preview && preview.would_adopt && (
        <div className="mt-3 p-3 rounded bg-blue-50 border border-blue-200 text-xs text-blue-800" data-testid="adopt-preview">
          <p className="font-semibold mb-1">Apercu :</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>Dossier source : <code className="bg-white px-1 rounded">{preview.would_adopt.current_name}</code></li>
            <li>Sera deplace dans : <b>{preview.would_adopt.target_section}</b></li>
            <li>Sera renomme : <code className="bg-white px-1 rounded">{targetNameOverride.trim() || preview.would_adopt.target_name}</code></li>
            {preview.would_adopt.project_city_missing && (
              <li className="text-amber-700">
                ⚠ La ville (geographic_zone) du projet est vide. Le nom auto genere ne contient pas de ville, editez-le ci-dessus si besoin.
              </li>
            )}
            {preview.previous_mapping?.root_folder_id && (
              <li className="text-amber-700">
                ⚠ Ancien dossier ({preview.previous_mapping.project_name || preview.previous_mapping.root_folder_id}) sera archive.
              </li>
            )}
          </ul>
        </div>
      )}

      {res && res.ok && (
        <div className="mt-3 p-3 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="adopt-result">
          <p className="font-semibold">✓ Dossier adopte et lie au projet.</p>
          <p className="mt-1">
            Nouveau dossier : <a href={res.mapping?.root_folder_url} target="_blank" rel="noreferrer" className="underline">
              {res.mapping?.project_name}
            </a>
          </p>
          {res.archived_previous && (
            <p className="mt-1">Ancien dossier archive : {res.archived_previous.new_archive_name}</p>
          )}
          {(res.reused_subfolders || []).length > 0 && (
            <p className="mt-1">Sous-dossiers reutilises (existants) : {res.reused_subfolders.map((m) => `${m.existing_name} -> ${m.canonical}`).join(', ')}</p>
          )}
          {(res.added_subfolders || []).length > 0 && (
            <p className="mt-1">Sous-dossiers ajoutes (manquants) : {res.added_subfolders.join(', ')}</p>
          )}
        </div>
      )}

      <DedupeProjectFolderForm projects={projects} />
    </div>
  );
};

/**
 * Iter 113k : outil pour fusionner les sous-dossiers dupliques d'un projet
 * Drive (ex: "01_Admin" + "01 - Admin" cote a cote apres une adoption qui a
 * echoue a matcher les anciens noms).
 */
const DedupeProjectFolderForm = ({ projects }) => {
  const [projectId, setProjectId] = React.useState('');
  const [plan, setPlan] = React.useState(null);
  const [res, setRes] = React.useState(null);
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);

  const runPreview = async () => {
    setErr(null); setPlan(null); setRes(null);
    if (!projectId) { setErr('Selectionnez un projet.'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/drive/branch-restructure/dedupe-project-folder`, {
        project_id: projectId, dry_run: true,
      });
      setPlan(r.data);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  const runExecute = async () => {
    if (!plan) return;
    const total = plan.total_duplicate_groups || 0;
    if (total === 0) { alert('Aucun doublon detecte.'); return; }
    if (!window.confirm(`Fusionner ${total} groupe(s) de doublons ?\nLes fichiers seront deplaces vers le sous-dossier canonique et les doublons mis a la corbeille (recuperables 30j).`)) return;
    setErr(null); setRes(null); setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/drive/branch-restructure/dedupe-project-folder`, {
        project_id: projectId, dry_run: false,
      });
      setRes(r.data); setPlan(null);
    } catch (e) {
      setErr(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  return (
    <div className="mt-6 pt-6 border-t border-gray-200" data-testid="drive-dedupe-form">
      <h4 className="font-semibold text-gray-800 text-sm mb-1">
        Nettoyer les doublons de sous-dossiers d&apos;un projet
        <span className="ml-2 inline-block text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-semibold">ITER 113K</span>
      </h4>
      <p className="text-xs text-gray-500 mb-3">
        Detecte les sous-dossiers dupliques (ex: &laquo;&nbsp;01_Admin&nbsp;&raquo; + &laquo;&nbsp;01 - Admin&nbsp;&raquo; cote a cote) et fusionne les contenus dans un seul dossier canonique.
        Les doublons sont mis a la corbeille Drive (recuperables 30 jours).
      </p>

      <div className="flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[280px]">
          <label className="block text-[11px] font-semibold text-gray-600 mb-1">Projet</label>
          <select
            value={projectId}
            onChange={(e) => { setProjectId(e.target.value); setPlan(null); setRes(null); }}
            className="w-full text-sm border border-gray-300 rounded px-2 py-1.5 bg-white"
            data-testid="dedupe-project-select"
          >
            <option value="">-- Choisir un projet --</option>
            {projects.map((p) => (
              <option key={p.project_id} value={p.project_id}>
                {p.name || p.project_id} {p.branch ? `(${p.branch})` : ''}
              </option>
            ))}
          </select>
        </div>
        <button
          type="button"
          onClick={runPreview}
          disabled={busy || !projectId}
          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-gray-100 text-gray-700 hover:bg-gray-200 disabled:opacity-50"
          data-testid="dedupe-preview-btn"
        >
          {busy && !plan ? '...' : 'Detecter'}
        </button>
        <button
          type="button"
          onClick={runExecute}
          disabled={busy || !plan || (plan.total_duplicate_groups || 0) === 0}
          className="px-3 py-1.5 rounded-md text-xs font-semibold bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          data-testid="dedupe-execute-btn"
        >
          {busy && plan ? 'Fusion en cours...' : 'Fusionner'}
        </button>
      </div>

      {err && <div className="mt-3 p-2 rounded bg-red-50 border border-red-200 text-xs text-red-700">{err}</div>}

      {plan && (
        <div className="mt-3 p-3 rounded bg-blue-50 border border-blue-200 text-xs text-blue-800" data-testid="dedupe-plan">
          <p className="font-semibold mb-1">
            {plan.total_duplicate_groups === 0 ? 'Aucun doublon detecte.' :
             `${plan.total_duplicate_groups} groupe(s) de doublons detecte(s) :`}
          </p>
          {[...(plan.root_duplicates || []), ...(plan.client_duplicates || [])].map((g, i) => (
            <div key={i} className="mt-1 pl-2 border-l-2 border-blue-300">
              <span className="font-semibold">Canonique :</span> <code className="bg-white px-1 rounded">{g.canonical.name}</code>
              <br />
              <span className="text-gray-600">Doublons a fusionner :</span> {g.duplicates.map((d) => d.name).join(', ')}
            </div>
          ))}
        </div>
      )}

      {res && (
        <div className="mt-3 p-3 rounded bg-emerald-50 border border-emerald-200 text-xs text-emerald-800" data-testid="dedupe-result">
          <p className="font-semibold">✓ Fusion terminee : {res.total_merged} doublon(s) traite(s).</p>
          <ul className="mt-1 list-disc list-inside">
            {(res.merged || []).map((m, i) => (
              <li key={i}>{m.duplicate_name} -&gt; {m.merged_into} ({m.files_moved} fichier(s) deplace(s))</li>
            ))}
          </ul>
          {(res.errors || []).length > 0 && (
            <p className="mt-1 text-red-700">Erreurs : {res.errors.length}</p>
          )}
        </div>
      )}
    </div>
  );
};

export default DriveBranchRestructurePanel;
